export type PoolJoinOutcome =
  | { status: 'joined' | 'canceled' }
  | { status: 'failed' | 'cleanup_failed'; error: Error };

export async function reconcilePoolJoin(deps: {
  target: string;
  join: (campaignId: string) => Promise<void>;
  leave: (campaignId: string) => Promise<void>;
  desired: () => string | null;
}): Promise<PoolJoinOutcome> {
  for (;;) {
    try {
      await deps.join(deps.target);
    } catch (error) {
      return { status: 'failed', error: error instanceof Error ? error : new Error(String(error)) };
    }
    if (deps.desired() === deps.target) return { status: 'joined' };
    try {
      await deps.leave(deps.target);
    } catch (error) {
      return { status: 'cleanup_failed', error: error instanceof Error ? error : new Error(String(error)) };
    }
    if (deps.desired() !== deps.target) return { status: 'canceled' };
  }
}

// A join refused because the switch had no registration for the extension.
// Servers that hold the seat (member.linePending) never answer this; it only
// matters while a deploy is rolling out and an older server still refuses
// with 409, and can be removed once every server holds seats. The phone was
// never rung, so it is a precondition to wait out, not a line fault.
export function isLinePreconditionFailure(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  return (error as { status?: unknown }).status === 409 && /not currently registered/i.test(error.message);
}

const LINE_FAULT_TENURE_MS = 45_000;
const LINE_FAULT_TRIPS = 2;

export function createLineHealthBreaker() {
  let joinedAtMs: number | null = null;
  let lineCameUp = false;
  let waitingForLine = false;
  let quickDrops = 0;
  return {
    joined(nowMs: number): void {
      if (joinedAtMs !== null) return;
      joinedAtMs = nowMs;
      lineCameUp = false;
      waitingForLine = false;
    },
    connecting(nowMs: number): void {
      joinedAtMs = nowMs;
      lineCameUp = false;
      waitingForLine = false;
    },
    // The server is holding the seat until this phone re-registers: no line
    // has been rung, so the tenure clock is not re-armed. A wait the server
    // gives up on counts as one fault however long the seat was held; a wait
    // that comes up is a live line like any other.
    waitingForLine(): void {
      waitingForLine = true;
    },
    lineUp(): void {
      lineCameUp = true;
      waitingForLine = false;
    },
    reset(): void {
      joinedAtMs = null;
      lineCameUp = false;
      waitingForLine = false;
      quickDrops = 0;
    },
    vanished(nowMs: number, busyWithNonPoolCall: boolean): boolean {
      if (joinedAtMs === null) return false;
      const quickFault = waitingForLine || (nowMs - joinedAtMs < LINE_FAULT_TENURE_MS && !lineCameUp);
      joinedAtMs = null;
      lineCameUp = false;
      waitingForLine = false;
      if (busyWithNonPoolCall) {
        quickDrops = 0;
        return false;
      }
      quickDrops = quickFault ? quickDrops + 1 : 0;
      if (quickDrops < LINE_FAULT_TRIPS) return false;
      quickDrops = 0;
      return true;
    },
  };
}
